import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { Trans } from 'react-i18next';

import { ACTOR_PHRASES } from '../metadata/actor_sentence';
import AgentMetadata from '../metadata/agent_metadata';
import Communicator from 'models/communicator';
import NavigateToItem from 'actions/conversation_item/navigate_to_item';
import ConversationItem from 'models/conversation_item';
import ItemContent from '../item_content';
import { iconClassNameForItem } from '../metadata/metadata_icon_classes';
import Metadata from '../metadata/metadata';
import { TaskLink } from './task_routing_item';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export default function TaskReopenedItem({ item }) {
  const executeAction = useExecuteAction();
  const onClick = useCallback(() => {
    executeAction(NavigateToItem, { customerId: item.customerId, itemId: item.content.taskItemId });
  }, [item.customerId, item.content.taskItemId]);

  if (item.initiator.type === Communicator.EXTERNAL) {
    return <TaskReopenedByReplyItem item={item} />;
  }

  return (
    <ItemContent isAuditItem item={item}>
      <AgentMetadata
        iconClass={iconClassNameForItem(item)}
        item={item}
        phrase={ACTOR_PHRASES.REOPENED_TASK}
        phraseComponents={{
          taskLink: (
            <a
              className="taskReopenedItem-link itemMetadata-subject"
              href={`#conversationItem-${item.content.taskItemId}`}
              onClick={onClick}
            />
          ),
        }}
      />
    </ItemContent>
  );
}

TaskReopenedItem.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

// A third party's reply into the task's side conversation reopened it. There is no agent to name, so
// the line names the task and the sender instead, the way a task becoming due is rendered. Like every
// audit item it sits on the outbound side, whoever acted.
function TaskReopenedByReplyItem({ item }) {
  return (
    <ItemContent isAuditItem isInbound={false} item={item}>
      <Metadata
        iconClass={iconClassNameForItem(item)}
        isInbound={false}
        text={
          <span className="taskReopenedItem">
            <TaskLink className="taskReopenedItem-link" taskItemId={item.content.taskItemId} />{' '}
            <Trans
              components={{ sender: <SenderAddress address={item.initiator.id} /> }}
              i18nKey="reopened by a reply from <sender/>"
            />
          </span>
        }
        timestamp={item.timestamp}
      />
    </ItemContent>
  );
}

TaskReopenedByReplyItem.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const BIDI_CONTROLS = /[‪-‮⁦-⁩]/g;
const MAX_SENDER_LENGTH = 80;

// The sender is the unauthenticated From header of an inbound email. It is rendered from a prop
// rather than as text so the metadata's linkifier never sees it, and it is trimmed so a crafted
// address can neither reorder the line with bidi controls nor run it off the screen.
function SenderAddress({ address }) {
  return (
    <span className="taskReopenedItem-sender">
      {_.truncate(address.replace(BIDI_CONTROLS, ''), { length: MAX_SENDER_LENGTH })}
    </span>
  );
}

SenderAddress.propTypes = {
  address: PropTypes.string.isRequired,
};
